import { DynamicRequestForm, type TemplateField, type UserOption } from '@/components/features/ServiceRequests/DynamicRequestForm';
import { SignoffPanel, type SignoffData } from '@/components/features/ServiceRequests/SignoffPanel';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { formatDateForDisplay, formatDateTime } from '@/lib/utils';
import { type PageProps } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { ArrowLeft, Paperclip, Printer } from 'lucide-react';
import { route } from 'ziggy-js';

interface ShowPageProps extends PageProps {
    [key: string]: unknown;
    request: {
        id: string;
        public_id: string | null;
        title: string;
        status: string;
        priority: string;
        due_date: string | null;
        requester_notes: string | null;
        admin_notes: string | null;
        submitted_data: Record<string, unknown> | null;
        template_snapshot: { fields?: TemplateField[] } | null;
        template_name: string | null;
        requester: { id: string; name: string } | null;
        assignee: { id: string; name: string } | null;
        created_at: string | null;
        updated_at: string | null;
        attachments: Array<{ id: string; file_name: string; mime_type: string | null; file_size: number | null; download_url: string }>;
        events: Array<{
            id: string;
            event_type: string;
            old_value: Record<string, unknown> | null;
            new_value: Record<string, unknown> | null;
            note: string | null;
            actor: string | null;
            created_at: string | null;
        }>;
        approval: SignoffData | null;
    };
    users: UserOption[];
}

const statusBadgeClassMap: Record<string, string> = {
    open: 'bg-info-subtle text-info-subtle-foreground',
    in_progress: 'bg-cat-purple text-cat-purple-foreground border-cat-purple-foreground/30',
    on_hold: 'bg-warning-subtle text-warning-subtle-foreground',
    resolved: 'bg-success-subtle text-success-subtle-foreground',
    closed: 'bg-muted text-muted-foreground',
    rejected: 'bg-danger-subtle text-danger-subtle-foreground',
};

const priorityBadgeClassMap: Record<string, string> = {
    low: 'bg-muted text-muted-foreground',
    normal: 'bg-info-subtle text-info-subtle-foreground',
    high: 'bg-warning-subtle text-warning-subtle-foreground',
    urgent: 'bg-danger-subtle text-danger-subtle-foreground',
};

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[140px_1fr] items-start gap-2 py-2.5 sm:grid-cols-[180px_1fr] sm:gap-4 sm:py-3">
            <div className="text-muted-foreground pt-0.5 text-sm">{label}</div>
            <div className="text-sm font-medium">{children}</div>
        </div>
    );
}

export default function ServiceRequestShowPage() {
    const { props } = usePage<ShowPageProps>();
    const { request, users } = props;
    const { t } = useTranslations();

    const fields: TemplateField[] = request.template_snapshot?.fields ?? [];
    const submittedData = request.submitted_data ?? {};
    const isFullySigned = request.approval?.status === 'approved';

    const breadcrumbs = [
        { title: t('service_requests.title', 'Service Requests'), href: '/service-requests' },
        { title: request.public_id ?? request.id.slice(0, 8), href: `/service-requests/${request.id}` },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={request.public_id ?? 'Service Request'} />

            <div className="space-y-6 p-4 sm:p-6 lg:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <Button variant="ghost" size="sm" asChild>
                        <Link href={route('service-requests.index')}>
                            <ArrowLeft className="mr-1 h-4 w-4" />
                            {t('service_requests.back_to_requests', 'Back to requests')}
                        </Link>
                    </Button>
                    {isFullySigned && (
                        <Button variant="outline" size="sm" asChild>
                            <a href={route('service-requests.pdf', request.id)} target="_blank" rel="noreferrer">
                                <Printer className="mr-1 h-4 w-4" />
                                {t('service_requests.signoff.print', 'Print clearance')}
                            </a>
                        </Button>
                    )}
                </div>

                <div className="bg-card rounded-xl border p-5">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="text-muted-foreground font-mono text-sm">{request.public_id}</span>
                        <Badge variant="outline" className={statusBadgeClassMap[request.status] ?? ''}>
                            {request.status}
                        </Badge>
                        <Badge variant="outline" className={priorityBadgeClassMap[request.priority] ?? ''}>
                            {request.priority}
                        </Badge>
                    </div>
                    <h1 className="text-xl font-bold">{request.title}</h1>
                    <div className="mt-4 divide-y">
                        <DetailRow label={t('service_requests.template', 'Template')}>{request.template_name ?? '—'}</DetailRow>
                        <DetailRow label={t('service_requests.assignee', 'Assignee')}>
                            {request.assignee?.name ?? (
                                <span className="text-muted-foreground">{t('service_requests.unassigned', 'Unassigned')}</span>
                            )}
                        </DetailRow>
                        <DetailRow label={t('service_requests.due_date', 'Due Date')}>
                            {request.due_date ? formatDateForDisplay(request.due_date) : <span className="text-muted-foreground">—</span>}
                        </DetailRow>
                        <DetailRow label={t('service_requests.created_at', 'Submitted')}>
                            {request.created_at ? formatDateTime(request.created_at) : '—'}
                        </DetailRow>
                    </div>
                </div>

                {request.approval && <SignoffPanel requestId={request.id} approval={request.approval} />}

                {fields.length > 0 && (
                    <div className="bg-card rounded-xl border p-5">
                        <h2 className="mb-5 text-sm font-semibold tracking-wide uppercase">
                            {t('service_requests.submitted_data', 'Request Details')}
                        </h2>
                        <DynamicRequestForm fields={fields} data={submittedData} users={users} onChange={() => {}} readOnly />
                    </div>
                )}

                {request.requester_notes && (
                    <div className="bg-card rounded-xl border p-5">
                        <h2 className="mb-3 text-sm font-semibold tracking-wide uppercase">
                            {t('service_requests.requester_notes', 'Additional notes')}
                        </h2>
                        <p className="text-sm leading-7 whitespace-pre-wrap">{request.requester_notes}</p>
                    </div>
                )}

                {request.admin_notes && (
                    <div className="bg-card rounded-xl border p-5">
                        <h2 className="mb-3 text-sm font-semibold tracking-wide uppercase">
                            {t('service_requests.admin_notes', 'Notes from admin')}
                        </h2>
                        <p className="text-sm leading-7 whitespace-pre-wrap">{request.admin_notes}</p>
                    </div>
                )}

                <div className="bg-card rounded-xl border p-5">
                    <div className="mb-3 flex items-center gap-2">
                        <Paperclip className="text-muted-foreground h-4 w-4" />
                        <h2 className="text-sm font-semibold tracking-wide uppercase">{t('service_requests.attachments', 'Attachments')}</h2>
                    </div>
                    {request.attachments.length === 0 ? (
                        <p className="text-muted-foreground text-sm">{t('service_requests.no_attachments', 'No attachments.')}</p>
                    ) : (
                        <div className="space-y-2">
                            {request.attachments.map((a) => (
                                <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3">
                                    <div>
                                        <p className="text-sm font-medium">{a.file_name}</p>
                                        <p className="text-muted-foreground text-xs">
                                            {a.mime_type ?? 'file'}
                                            {a.file_size ? ` · ${(a.file_size / 1024).toFixed(1)} KB` : ''}
                                        </p>
                                    </div>
                                    <Button asChild variant="outline" size="sm">
                                        <a href={a.download_url} target="_blank" rel="noreferrer">
                                            {t('service_requests.download', 'Download')}
                                        </a>
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-card rounded-xl border p-5">
                    <h2 className="mb-4 text-sm font-semibold tracking-wide uppercase">{t('service_requests.activity', 'Activity')}</h2>
                    {request.events.length === 0 ? (
                        <p className="text-muted-foreground text-sm">{t('service_requests.no_activity', 'No activity yet.')}</p>
                    ) : (
                        <div className="space-y-3">
                            {request.events.map((e) => (
                                <div key={e.id} className="rounded-lg border p-3">
                                    <p className="text-sm font-medium">{e.event_type}</p>
                                    <p className="text-muted-foreground mt-0.5 text-xs">
                                        {e.actor ?? t('service_requests.system', 'System')} · {e.created_at ? formatDateTime(e.created_at) : '—'}
                                    </p>
                                    {e.note && <p className="mt-2 text-sm">{e.note}</p>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
}
